const { Core, MediaQueries } = Global;

const AUTOPLAY_INTERVAL = 3000;
const TRANSITION_FALLBACK = 500;
const RESETTING_CLASS = '@resetting';

if (!customElements.get('utility-bar-carousel')) {
    customElements.define('utility-bar-carousel', class extends Core {
        elements = {
            track: '[data-element="track"]'
        }

        render() {
            this.track = this.$('track');
            if (!this.track) return;

            this.items = Array.from(this.track.children)
                .filter(item => item.classList.contains('#utility-bar-item'));
            this.mobileItems = this.items
                .filter(item => item.classList.contains('left'));
            this.currentIndex = 0;
            this.mobileCarouselEnabled = false;
            this.pauseReasons = new Set();
            this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

            this._handleMediaChange = this._handleMediaChange.bind(this);
            this._handleReducedMotionChange = this._handleReducedMotionChange.bind(this);
            this._handleTransitionEnd = this._handleTransitionEnd.bind(this);
            this._handleVisibilityChange = this._handleVisibilityChange.bind(this);
            this._pauseOnHover = () => this._pauseForInteraction('hover');
            this._resumeAfterHover = () => this._setPaused('hover', false);
            this._pauseOnFocus = () => this._pauseForInteraction('focus');
            this._resumeAfterFocus = event => {
                if (!this.contains(event.relatedTarget)) {
                    this._setPaused('focus', false);
                }
            };

            MediaQueries.MOBILE.addEventListener('change', this._handleMediaChange);
            this.reducedMotion.addEventListener('change', this._handleReducedMotionChange);
            this.track.addEventListener('transitionend', this._handleTransitionEnd);
            document.addEventListener('visibilitychange', this._handleVisibilityChange);
            this.addEventListener('mouseenter', this._pauseOnHover);
            this.addEventListener('mouseleave', this._resumeAfterHover);
            this.addEventListener('focusin', this._pauseOnFocus);
            this.addEventListener('focusout', this._resumeAfterFocus);

            this._handleVisibilityChange();
            this._handleMediaChange(MediaQueries.MOBILE);
        }

        destroy() {
            this._stopAutoplay();
            this._cancelTransition();

            MediaQueries.MOBILE.removeEventListener('change', this._handleMediaChange);
            this.reducedMotion.removeEventListener('change', this._handleReducedMotionChange);
            this.track?.removeEventListener('transitionend', this._handleTransitionEnd);
            document.removeEventListener('visibilitychange', this._handleVisibilityChange);
            this.removeEventListener('mouseenter', this._pauseOnHover);
            this.removeEventListener('mouseleave', this._resumeAfterHover);
            this.removeEventListener('focusin', this._pauseOnFocus);
            this.removeEventListener('focusout', this._resumeAfterFocus);

            this._resetTrack();
            this._resetAccessibility();
        }

        _handleMediaChange({ matches }) {
            if (matches) {
                this._enableMobileCarousel();
            } else {
                this._disableMobileCarousel();
            }
        }

        _handleReducedMotionChange() {
            this._stopAutoplay();
            this._startAutoplay();
        }

        _enableMobileCarousel() {
            if (this.mobileCarouselEnabled) return;

            this.mobileCarouselEnabled = true;
            this.currentIndex = 0;
            this._resetTrack();
            this._updateAccessibility();
            this._startAutoplay();
        }

        _disableMobileCarousel() {
            if (!this.mobileCarouselEnabled) return;

            this.mobileCarouselEnabled = false;
            this._stopAutoplay();
            this._cancelTransition();
            this.currentIndex = 0;
            this._resetTrack();
            this._resetAccessibility();
        }

        _startAutoplay() {
            if (
                this.autoplayTimer
                || !this.mobileCarouselEnabled
                || this.mobileItems.length < 2
                || this.reducedMotion.matches
                || this.pauseReasons.size
            ) return;

            this.autoplayTimer = window.setInterval(() => this._showNextItem(), AUTOPLAY_INTERVAL);
        }

        _stopAutoplay() {
            window.clearInterval(this.autoplayTimer);
            this.autoplayTimer = null;
        }

        _showNextItem() {
            if (this.transitioning || !this.mobileCarouselEnabled) return;

            const nextIndex = (this.currentIndex + 1) % this.mobileItems.length;
            this.transitioning = true;
            this.nextIndex = nextIndex;
            this.wrapping = nextIndex === 0;

            if (this.wrapping) {
                this.mobileItems[0].style.transform = `translateX(${this.mobileItems.length * 100}%)`;
                this.transitionFrame = window.requestAnimationFrame(() => {
                    this.transitionFrame = null;
                    if (!this.mobileCarouselEnabled) return;
                    this._moveTrack(this.mobileItems.length);
                    this._setTransitionFallback();
                });
                return;
            }

            this._moveTrack(nextIndex);
            this._setTransitionFallback();
        }

        _moveTrack(index) {
            this.track.style.setProperty('--utility-bar-slide-offset', `${index * -100}%`);
        }

        _setTransitionFallback() {
            const fallbackDelay = this.reducedMotion.matches ? 0 : TRANSITION_FALLBACK;
            this.transitionFallback = window.setTimeout(() => this._finishTransition(), fallbackDelay);
        }

        _handleTransitionEnd(event) {
            if (event.target === this.track && event.propertyName === 'transform') {
                this._finishTransition();
            }
        }

        _finishTransition() {
            if (!this.transitioning) return;

            window.clearTimeout(this.transitionFallback);
            this.transitionFallback = null;

            if (this.wrapping) {
                this.track.classList.add(RESETTING_CLASS);
                this._moveTrack(0);
                this.mobileItems[0].style.removeProperty('transform');
                // Commit the invisible reset before transitions are enabled again.
                this.track.getBoundingClientRect();
                this.resetFrame = window.requestAnimationFrame(() => {
                    this.resetFrame = null;
                    this.track.classList.remove(RESETTING_CLASS);
                });
            }

            this.currentIndex = this.nextIndex;
            this.nextIndex = null;
            this.wrapping = false;
            this.transitioning = false;
            this._updateAccessibility();
        }

        _cancelTransition() {
            window.clearTimeout(this.transitionFallback);
            window.cancelAnimationFrame(this.transitionFrame);
            window.cancelAnimationFrame(this.resetFrame);
            this.transitionFallback = null;
            this.transitionFrame = null;
            this.resetFrame = null;
            this.nextIndex = null;
            this.wrapping = false;
            this.transitioning = false;
        }

        _pauseForInteraction(reason) {
            this._setPaused(reason, true);
            if (!this.transitioning) return;

            this._cancelTransition();
            this.track.classList.add(RESETTING_CLASS);
            this._moveTrack(this.currentIndex);
            this.mobileItems[0].style.removeProperty('transform');
            this.track.getBoundingClientRect();
            this.track.classList.remove(RESETTING_CLASS);
        }

        _resetTrack() {
            if (!this.track) return;

            this.track.classList.add(RESETTING_CLASS);
            this.track.style.removeProperty('--utility-bar-slide-offset');
            this.mobileItems?.[0]?.style.removeProperty('transform');
            this.track.getBoundingClientRect();
            this.track.classList.remove(RESETTING_CLASS);
        }

        _updateAccessibility() {
            const activeItem = this.mobileItems[this.currentIndex];

            this.items.forEach(item => {
                const isActive = item === activeItem;
                item.toggleAttribute('inert', !isActive);
                if (isActive) {
                    item.removeAttribute('aria-hidden');
                } else {
                    item.setAttribute('aria-hidden', 'true');
                }
            });
        }

        _resetAccessibility() {
            this.items?.forEach(item => {
                item.removeAttribute('aria-hidden');
                item.removeAttribute('inert');
            });
        }

        _setPaused(reason, paused) {
            if (paused) {
                this.pauseReasons.add(reason);
                this._stopAutoplay();
                return;
            }

            this.pauseReasons.delete(reason);
            this._startAutoplay();
        }

        _handleVisibilityChange() {
            this._setPaused('document-hidden', document.hidden);
        }
    });
}
